import type { StateChangeRule } from './chartStateRules';

/**
 * Applies a state change rule to the current state
 * @param currentState - Current chart state
 * @param rule - State change rule to apply
 * @param payload - Payload data for the state change
 * @returns New state or null if validation fails
 */
export function applyStateChange<T>(
  currentState: ChartState,
  rule: StateChangeRule<T>,
  payload: T,
): ChartState | null {
  // 1. Validation
  if (rule.validate && !rule.validate(currentState, payload)) {
    return null;
  }

  // 2. Transform state
  const transformed = rule.transform(currentState, payload);

  // 3. Merge with current state
  const newState = {
    ...currentState,
    ...transformed,
  };

  return newState;
}

/**
 * Applies a state change rule with side effects (like showing popups)
 * Used when the state change requires user confirmation or has external effects
 * @param currentState - Current chart state
 * @param rule - State change rule to apply
 * @param payload - Payload data for the state change
 * @param setState - Function to update the state
 * @returns Whether the state change was applied immediately
 */
export function applyStateChangeWithSideEffects<T>(
  currentState: ChartState,
  rule: StateChangeRule<T>,
  payload: T,
  setState: (state: ChartState | ((prev: ChartState) => ChartState)) => void,
): boolean {
  // 1. Validation
  if (rule.validate && !rule.validate(currentState, payload)) {
    return false;
  }

  // 2. Check if there are side effects
  if (rule.sideEffects) {
    // Create a callback for when side effect confirms the action
    const onConfirm = () => {
      const transformed = rule.transform(currentState, payload);
      setState((prev) => ({
        ...prev,
        ...transformed,
      }));
    };

    // Execute side effects (may call onConfirm async)
    const hasSideEffects = rule.sideEffects(currentState, payload, onConfirm);
    if (hasSideEffects) {
      return false;
    }
  }

  // 3. No side effects - apply immediately
  const transformed = rule.transform(currentState, payload);
  setState((prev) => ({
    ...prev,
    ...transformed,
  }));

  return true;
}

/**
 * Batch applies multiple state changes
 * Useful when multiple related changes need to happen together
 * @param currentState - Current chart state
 * @param changes - Array of rules and payloads to apply
 * @returns New state with all changes applied
 */
export function applyBatchStateChanges(
  currentState: ChartState,
  changes: Array<{
    rule: StateChangeRule<any>;
    payload: any;
  }>,
): ChartState | null {
  let state = currentState;

  for (const { rule, payload } of changes) {
    const newState = applyStateChange(state, rule, payload);
    if (newState === null) {
      // If any change fails validation, abort the batch
      return null;
    }
    state = newState;
  }

  return state;
}
